import React, { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import styled from "styled-components";
import { setColor, setFont } from "../../../../../assets/styles/styles";
import Typography from "../../../../../components/common/text/Typography";
import Title from "../../../components/Title";
import ToggleSwitch from "../../../../../components/common/switchToggle/ToggleSwitch";
import Modal from "../../../components/Modal";
import JsonViewerModal from "../../vanityLinks/components/JsonViewerModal";
import Button from "../../../../../components/common/button/Button";
import UploadFile from "../../../../../components/common/fileUploader/FileUploaderInput";
import Dropdown from "../../../../../components/common/inputDropdown/Dropdown";
import { getAllRoomPreset } from "../../../../../api/ClientsTagController";
import { CACHE_QUERY_OPTIONS } from "../../../../../constants";
import RoomSearchDropdown from "./RoomSearchDropdown";
import { getRooms } from "../../rooms/api/roomController";
import { getAchievements } from "../../../../../api/AchievementController";
import AddRoomModal from "../../rooms/components/AddRoom";
import AddAchievement from "../../achievements/AddAchievement";

const CreateTag = ({
    tagState,
    setTagState,
    tagSettingState,
    setTagSettingState,
    step,
    setStep,
    uploadLoadingScreenImage,
}) => {
    // states for updating tag
    const [isJsonEditorOpen, setIsJsonEditorOpen] = useState(false);
    const [jsonEditorValue, setJsonEditorValue] = useState("");
    const [jsonEditorField, setJsonEditorField] = useState("");

    const [isAddRoomModalOpen, setIsAddRoomModalOpen] = useState(false);
    const [isAddAchievementModalOpen, setIsAddAchievementModalOpen] = useState(false);

    const [buttonDisabled, setButtonDisabled] = useState(true);

    // state for updating setting
    const [selectedBGImg, setSelectedBGImg] = useState(null);
    const [errorMessage, setErrorMessage] = useState(false);

    const updateTagState = (key, value) => {
        if (key === "name" && value.trim() === "") {
            setButtonDisabled(true);
        } else {
            setButtonDisabled(false);
        }

        setTagState((prevState) => ({
            ...prevState,
            [key]: value,
        }));
    };

    const { data: presetData } = useQuery(["roomPresets"], getAllRoomPreset, {
        ...CACHE_QUERY_OPTIONS(),
    });

    const {
        data: roomOptions,
        isLoading: roomDataLoading,
        refetch: refetchRooms,
    } = useQuery(["rooms"], getRooms, {
        ...CACHE_QUERY_OPTIONS(),
    });

    const { data: achievementOptions, refetch: refetchAchievements } = useQuery(["achievements"], getAchievements, {
        ...CACHE_QUERY_OPTIONS(),
    });

    useEffect(() => {
        console.log("current tag state update: ", tagState);
    }, [tagState]);

    const setPresetData = (value) => {
        const selectedPreset = presetData?.roomPresets?.find((item) => item.presetName === value);
        updateTagState("presetId", selectedPreset?.presetId || "");
        updateTagSettingState("settingPresetName", value || "");
    };

    const updateTagSettingState = (key, value) => {
        if (key === "settingPresetName" && value.trim() === "") {
            setButtonDisabled(true);
        } else {
            setButtonDisabled(false);
        }

        if (key.includes(".")) {
            const [parent, child] = key.split(".");
            setTagSettingState((prevState) => ({
                ...prevState,
                [parent]: {
                    ...prevState[parent],
                    [child]: value,
                },
            }));
        } else {
            setTagSettingState((prevState) => ({
                ...prevState,
                [key]: value,
            }));
        }
        // console.log("Updated Tag State:", { ...tagSettingState, [key]: value });
    };

    useEffect(() => {
        if (!isAddRoomModalOpen) {
            refetchRooms();
        }
    }, [isAddRoomModalOpen, refetchRooms]);

    useEffect(() => {
        if (!isAddRoomModalOpen) {
            refetchAchievements();
        }
    }, [isAddRoomModalOpen, refetchAchievements]);

    return (
        <>
            <ModalContent>
                <GeneralContainer>
                    <Typography variant="h2PurpleHeading" margin={`1rem 0 0 0`} fontSize={"24px"}>
                        {" "}
                        Step 1: Create Client Tag
                    </Typography>
                    <Table>
                        <TableRow>
                            <TableColumn>
                                <Label>Client Tag Name*</Label>
                                <InputBox
                                    type="text"
                                    id="clientTagName"
                                    placeholder="Enter Client Tag Name"
                                    value={tagState.clientTag}
                                    onChange={(e) => updateTagState("clientTag", e.target.value)}
                                />
                            </TableColumn>
                        </TableRow>

                        {!roomDataLoading && (roomOptions !== null || roomOptions !== undefined) && (
                            <TableRow>
                                <TableColumn>
                                    <RoomSearchDropdown
                                        label={"Search Room"}
                                        updateTagState={updateTagState}
                                        selectedRoom={Array.isArray(tagState.roomIds) ? tagState.roomIds : []}
                                    />
                                    <Row>
                                        <TextP>
                                            Can't find the room you're looking for?
                                            <Button
                                                variant={"NavigationButton"}
                                                fontSize={"12px"}
                                                margin={"5px 0 0 10px"}
                                                label={"Create New Room"}
                                                onClick={() => setIsAddRoomModalOpen(true)}
                                            />
                                        </TextP>
                                    </Row>
                                    <TagsContainer>
                                        {(Array.isArray(tagState.roomIds) ? tagState.roomIds : []).map((id) => (
                                            <Tag key={id}>
                                                <span>{roomOptions.find((room) => room.id === id)?.name || id}</span>
                                                <RemoveButton
                                                    onClick={() => {
                                                        updateTagState(
                                                            "roomIds",
                                                            (Array.isArray(tagState.roomIds)
                                                                ? tagState.roomIds
                                                                : []
                                                            ).filter((item) => item !== id)
                                                        );
                                                    }}
                                                    aria-label="Remove"
                                                >
                                                    ×
                                                </RemoveButton>
                                            </Tag>
                                        ))}
                                    </TagsContainer>
                                </TableColumn>
                            </TableRow>
                        )}

                        {!roomDataLoading && achievementOptions && Array.isArray(achievementOptions) && (
                            <TableRow>
                                <TableColumn>
                                    <Dropdown
                                        label="Achievement"
                                        items={achievementOptions.map((ach) => ({
                                            value: ach.title,
                                        }))}
                                        selectedValue={""}
                                        setSelectedValue={(value) => {
                                            if (!tagState.achievementIds?.includes(value)) {
                                                updateTagState("achievementIds", [
                                                    ...(tagState.achievementIds || []),
                                                    achievementOptions.find((ach) => ach.title === value)?.id,
                                                ]);
                                            }
                                        }}
                                        placeholder="Select Achievement"
                                    />
                                    <Row>
                                        <TextP>
                                            Can't find the achievement you're looking for?
                                            <Button
                                                variant={"NavigationButton"}
                                                fontSize={"12px"}
                                                margin={"5px 0 0 10px"}
                                                label={"Create New Achievement"}
                                                // onClick={handleAddRoomTypeClick}
                                            />
                                        </TextP>
                                    </Row>
                                    <TagsContainer>
                                        {(tagState.achievementIds || []).map((id) => (
                                            <Tag key={id}>
                                                <span>
                                                    {achievementOptions.find((ach) => ach.id === id)?.title || id}
                                                </span>
                                                <RemoveButton
                                                    onClick={() => {
                                                        updateTagState(
                                                            "achievementIds",
                                                            tagState.achievementIds.filter((item) => item !== id)
                                                        );
                                                    }}
                                                    aria-label="Remove"
                                                >
                                                    ×
                                                </RemoveButton>
                                            </Tag>
                                        ))}
                                    </TagsContainer>
                                </TableColumn>
                            </TableRow>
                        )}
                        <TableRow>
                            <TableColumn>
                                <Dropdown
                                    key={"presetDataInput"}
                                    label="Room Preset*"
                                    items={presetData?.roomPresets?.map((item) => ({
                                        ...item,
                                        value: item.presetName,
                                    }))}
                                    selectedValue={tagState.presetId}
                                    setSelectedValue={setPresetData}
                                    placeholder="Select Room Preset"
                                />
                            </TableColumn>
                        </TableRow>

                        <TableRow>
                            <TableColumn>
                                <Label>Client Tag Meta data</Label>
                                <InputBox
                                    type="text"
                                    name={"meta"}
                                    value={JSON.stringify(tagState.clientTagMeta || {})}
                                    onClick={() => {
                                        setIsJsonEditorOpen(true);
                                        setJsonEditorField("clientTagMeta");
                                        setJsonEditorValue(JSON.stringify(tagState.clientTagMeta || {}, null, 2));
                                    }}
                                />
                            </TableColumn>
                        </TableRow>

                        <TableRow>
                            <TableColumn>
                                <UploadFile
                                    label="loading screen Image"
                                    accept={["image/png", "image/jpeg", "image/jpg"]}
                                    selectedFile={selectedBGImg}
                                    setSelectedFile={setSelectedBGImg}
                                    source="withoutCallingFileUploadAPI"
                                />
                                {errorMessage && (
                                    <TextContent hasError fontsize={"14px"}>
                                        {"File size should be less than 1MB"}
                                    </TextContent>
                                )}
                            </TableColumn>
                        </TableRow>

                        <TableRow>
                            <TableColumn>
                                <Label>Loading Message</Label>
                                <InputBox
                                    type="text"
                                    id="Client Tag Setting"
                                    placeholder="Loading Message"
                                    value={tagSettingState.loadingMessage.default}
                                    onChange={(e) => updateTagSettingState("loadingMessage.default", e.target.value)}
                                />
                            </TableColumn>
                        </TableRow>

                        <TableRow>
                            <TableColumn>
                                <Label>Login Text</Label>
                                <InputBox
                                    type="text"
                                    id="Client Tag Setting"
                                    placeholder="Login Text"
                                    value={tagSettingState.loginText}
                                    onChange={(e) => updateTagSettingState("loginText", e.target.value)}
                                />
                            </TableColumn>
                        </TableRow>

                        <TableRow>
                            <TableColumn>
                                <Label>login background image Url</Label>
                                <InputBox
                                    type="text"
                                    id="Client Tag Setting"
                                    placeholder="login background image Url"
                                    value={tagSettingState.loginUrl}
                                    onChange={(e) => updateTagSettingState("loginUrl", e.target.value)}
                                />
                            </TableColumn>
                        </TableRow>

                        <TableRow>
                            <TableColumn>
                                <Label>Client Tag Setting Meta data</Label>
                                <InputBox
                                    type="text"
                                    name={"metaData"}
                                    value={JSON.stringify(tagSettingState.metaData || {})}
                                    onClick={() => {
                                        setIsJsonEditorOpen(true);
                                        setJsonEditorField("metaData");
                                        setJsonEditorValue(JSON.stringify(tagSettingState.metaData || {}, null, 2));
                                    }}
                                />
                            </TableColumn>
                        </TableRow>

                        <TableRow>
                            <Button
                                // type="submit"
                                variant={"PinkButton"}
                                buttonWidth={"100%"}
                                disabled={buttonDisabled}
                                label={`Create Client Tag`}
                                onClick={async () => {
                                    if (selectedBGImg) {
                                        try {
                                            const res = await uploadLoadingScreenImage(selectedBGImg);
                                            console.log("Uploading loading screen image:", selectedBGImg);
                                            if (res?.status === 200 || res?.status === 201) {
                                                setStep(step + 1);
                                            } else {
                                                console.error("Error uploading loading screen image:", res);
                                                setErrorMessage(true);
                                            }
                                        } catch (error) {
                                            console.error("Error uploading loading screen image:", error);
                                            setErrorMessage(true);
                                        }
                                    } else {
                                        setStep(step + 1);
                                    }
                                }}
                            />
                        </TableRow>
                    </Table>
                </GeneralContainer>
            </ModalContent>

            {isJsonEditorOpen && (
                <Modal
                    isOpen={isJsonEditorOpen}
                    setIsOpen={setIsJsonEditorOpen}
                    children={
                        <JsonViewerModal
                            jsonEditorValue={jsonEditorValue}
                            handleJsonSave={(data) => {
                                if (jsonEditorField === "clientTagMeta") {
                                    setTagState((prev) => ({
                                        ...prev,
                                        clientTagMeta: data,
                                    }));
                                } else if (jsonEditorField === "metaData") {
                                    setTagSettingState((prev) => ({
                                        ...prev,
                                        metaData: data,
                                    }));
                                }
                                setIsJsonEditorOpen(false);
                            }}
                            fieldName={jsonEditorField}
                            label={jsonEditorField}
                            closeModal={() => setIsJsonEditorOpen(false)}
                        />
                    }
                />
            )}

            {isAddRoomModalOpen && (
                <Modal
                    isOpen={isAddRoomModalOpen}
                    setIsOpen={setIsAddRoomModalOpen}
                    children={<AddRoomModal closeAddModal={() => setIsAddRoomModalOpen(false)} />}
                />
            )}

            {isAddAchievementModalOpen && (
                <Modal
                    isOpen={isAddAchievementModalOpen}
                    setIsOpen={setIsAddAchievementModalOpen}
                    children={
                        <AddAchievement
                            closeAddModal={() => setIsAddAchievementModalOpen(false)}
                            refetchAchievements={refetchAchievements}
                        />
                    }
                />
            )}
        </>
    );
};

export default CreateTag;

const ModalContent = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    width: 100%;
    color: ${setColor.inputText};
`;

const GeneralContainer = styled.div`
    width: 80%;
    font-family: "Barlow-Regular", Helvetica;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    align-self: center;
    margin-top: 15px;
    @media (max-width: 768px) {
        font-size: 14px;
    }
`;

const Table = styled.div`
    display: flex;
    flex-direction: column;
    padding: 1rem;
    border-radius: 5px;
    color: #fbfcff;
`;

const TableRow = styled.div`
    display: flex;
    flex-direction: row;
    margin-bottom: 10px;
`;

const TableColumn = styled.div`
    flex: 1;
    padding: 5px;
`;

const Label = styled.div`
    font-weight: 600;
    font-size: 16px;
    margin-bottom: 10px;
`;

const InputBox = styled.input`
    padding: 10px;
    border: ${(props) => (props.errorMessage ? `1px solid ${setColor.errorColor}` : `1px solid ${setColor.grey1}`)};
    border-radius: 5px;
    color: ${setColor.inputText};
    margin: 0rem 0rem 0rem 0rem;
    width: ${(props) => (props.width ? props.width : "100%")};
    background-color: transparent;
    margin-bottom: ${(props) => (props.errorMessage ? "0.5rem" : "0rem")};
    &:disabled {
        background-color: ${setColor.grey1};
    }
`;

const TextContent = styled.p`
    color: ${(props) => (props.hasError ? setColor.errorColor : setColor.circularProgress)};
    text-align: left;
    font-family: ${setFont.barlow}, sans-serif;
    font-size: ${(props) => (props.fontsize ? props.fontsize : "13px")};
    font-style: normal;
    line-height: normal;
    margin: 5px;
    letter-spacing: 0.5px;
    font-weight: 400;

    @media (max-width: 768px) {
        // margin-bottom: 20px;
    }
`;

const TagsContainer = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 8px;
`;

const Tag = styled.div`
    display: flex;
    align-items: center;
    background: ${setColor.grey1};
    border-radius: 16px;
    padding: 4px 12px;
    margin-right: 8px;
    margin-bottom: 8px;
`;

const RemoveButton = styled.button`
    margin-left: 8px;
    background: transparent;
    border: none;
    cursor: pointer;
    color: #d00;
    font-weight: bold;
`;

const Row = styled.div`
    display: flex;
    margin: ${(props) => (props.marginBottom ? "1rem 0rem" : "0rem orem")};
`;

const TextP = styled.div`
    font-size: 14px;
`;
